// Shared helpers to turn Convex action/mutation errors into clean, user-facing messages.

// Extracts the first balanced JSON object from a string, ignoring braces inside
// string literals. Returns null when no complete object is found. Unlike a naive
// slice from the first '{' to the end, this tolerates trailing text after the
// object (e.g. `Upload failed: 400 - {"error":"x"} (retry 2)`).
function extractJsonObject(text: string): string | null {
  const start = text.indexOf('{');
  if (start < 0) {
    return null;
  }

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < text.length; i += 1) {
    const char = text[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
    } else if (char === '{') {
      depth += 1;
    } else if (char === '}') {
      depth -= 1;
      if (depth === 0) {
        return text.slice(start, i + 1);
      }
    }
  }

  return null;
}

// HTTP upload errors arrive as `... - {"error":"..."}` strings. Pull the inner
// error/message out of the embedded JSON body when present.
export function unwrapActionErrorMessage(message: string): string {
  const trimmed = message.trim();
  const jsonSlice = extractJsonObject(trimmed);
  if (jsonSlice) {
    try {
      const parsed = JSON.parse(jsonSlice) as { error?: unknown; message?: unknown };
      const payload = typeof parsed.error === 'string' ? parsed.error : parsed.message;
      if (typeof payload === 'string' && payload.trim().length > 0) {
        return payload.trim();
      }
    } catch {
      // Keep the original message when the suffix is not a JSON error payload.
    }
  }
  return trimmed;
}

export function getActionErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'string' && error.trim().length > 0) {
    return error.trim();
  }

  if (error && typeof error === 'object') {
    const maybeData = (error as { data?: unknown }).data;
    if (typeof maybeData === 'string' && maybeData.trim().length > 0) {
      return maybeData.trim();
    }

    if (maybeData && typeof maybeData === 'object') {
      const dataMessage = (maybeData as { message?: unknown }).message;
      if (typeof dataMessage === 'string' && dataMessage.trim().length > 0) {
        return dataMessage.trim();
      }
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    const message = unwrapActionErrorMessage(error.message);
    const uncaughtLine = message
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line.startsWith('Uncaught Error:'));

    if (uncaughtLine) {
      return uncaughtLine.replace('Uncaught Error:', '').trim();
    }

    const cleaned = message
      .replace(/\[CONVEX[^\]]*\]\s*/g, '')
      .replace(/\[Request ID:[^\]]*\]/g, '')
      .replace(/\bServer Error\b/g, '')
      .replace(/\bCalled by client\b/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

    if (cleaned.length > 0) {
      return cleaned;
    }
  }

  return fallback;
}
